import React, { useState, useEffect, useRef } from 'react';
import { Plus, X } from 'lucide-react';
import { TimeInput } from '../Common/TimeInput';
import OpeningHoursEditor from './OpeningHoursEditor';
import DateRangeListEditor from './DateRangeListEditor';
import FieldInfo from '../Common/FieldInfo';
import { validateDateRanges } from '../../utils/dateRanges';
import './EventScheduleManager.css';

const EVENT_TYPES = {
  REGULAR_HOURS: 'regular_hours',
  RECURRING_EVENT: 'recurring_event',
  SINGLE_EVENT: 'single_event',
  MULTI_DAY: 'multi_day',
  FREELY_ACCESSIBLE: 'freely_accessible'
};

const RECURRENCE_PATTERNS = {
  DAILY: 'daily',
  WEEKLY: 'weekly',
  BIWEEKLY: 'biweekly',
  TRIWEEKLY: 'triweekly',
  FOURWEEKLY: 'fourweekly',
  MONTHLY_FIRST: 'monthly_first',
  MONTHLY_SECOND: 'monthly_second',
  MONTHLY_THIRD: 'monthly_third',
  MONTHLY_FOURTH: 'monthly_fourth',
  MONTHLY_LAST: 'monthly_last'
};

const RECURRENCE_TYPES = {
  DAILY: 'daily_type',
  WEEKLY: 'weekly_type',
  MONTHLY: 'monthly_type'
};

const DAYS_DE = {
  monday: 'Montag',
  tuesday: 'Dienstag',
  wednesday: 'Mittwoch',
  thursday: 'Donnerstag',
  friday: 'Freitag',
  saturday: 'Samstag',
  sunday: 'Sonntag'
};

const EventScheduleManager = ({ 
  eventType, 
  openingHours, 
  eventSchedule, 
  isSeasonal,
  isEditMode = false,
  onEventTypeChange, 
  onOpeningHoursChange,
  onEventScheduleChange,
  onSeasonalChange,
  onImportOpeningHours,
  onImportFromWebsite,
  address,
  website,
  onWebsiteChange,
  canImportFromGoogle = false,
  onSaveStatusChange // New prop to notify parent of save status
}) => {
  const [localSchedule, setLocalSchedule] = useState(eventSchedule || {});
  const [recurrenceType, setRecurrenceType] = useState(RECURRENCE_TYPES.WEEKLY);
  const [dailySchedule, setDailySchedule] = useState({
    start_time: '',
    end_time: '',
    recurrence: RECURRENCE_PATTERNS.DAILY,
    additional_times: []
  });
  const [weeklySchedule, setWeeklySchedule] = useState({
    day_of_week: '',
    start_time: '',
    end_time: '',
    recurrence: RECURRENCE_PATTERNS.WEEKLY,
    additional_times: []
  });
  const [monthlySchedule, setMonthlySchedule] = useState({
    day_of_week: '',
    start_time: '',
    end_time: '',
    recurrence: RECURRENCE_PATTERNS.MONTHLY_FIRST,
    additional_times: []
  });
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [singleEventDates, setSingleEventDates] = useState([{ date: '', start_time: '09:00', end_time: '' }]);
  const [timeDataSaved, setTimeDataSaved] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Ref to skip resetting validated states on initial mount when event type is set from props
  const prevEventType = useRef(eventType);

  // Im Edit-Modus: Setze validated States direkt bei der Initialisierung
  const [dailyValidated, setDailyValidated] = useState(
    isEditMode && eventType === EVENT_TYPES.RECURRING_EVENT &&
    eventSchedule?.recurrence === RECURRENCE_PATTERNS.DAILY
  );
  const [weeklyValidated, setWeeklyValidated] = useState(
    isEditMode && eventType === EVENT_TYPES.RECURRING_EVENT &&
    [RECURRENCE_PATTERNS.WEEKLY, RECURRENCE_PATTERNS.BIWEEKLY, RECURRENCE_PATTERNS.TRIWEEKLY, RECURRENCE_PATTERNS.FOURWEEKLY].includes(eventSchedule?.recurrence)
  );
  const [monthlyValidated, setMonthlyValidated] = useState(
    isEditMode && eventType === EVENT_TYPES.RECURRING_EVENT &&
    !!eventSchedule?.recurrence?.startsWith('monthly_')
  );
  const [singleEventValidated, setSingleEventValidated] = useState(
    isEditMode && (eventType === EVENT_TYPES.SINGLE_EVENT || eventType === EVENT_TYPES.MULTI_DAY)
  );
  const [freelyAccessibleValidated, setFreelyAccessibleValidated] = useState(
    isEditMode && eventType === EVENT_TYPES.FREELY_ACCESSIBLE
  );

  useEffect(() => {
    setLocalSchedule(eventSchedule || {});
    
    // Initialize single event dates from eventSchedule
    if (eventSchedule?.dates && Array.isArray(eventSchedule.dates)) {
      setSingleEventDates(eventSchedule.dates);
    } else if (eventSchedule?.date) {
      // Legacy single date support
      setSingleEventDates([{
        date: eventSchedule.date,
        start_time: eventSchedule.start_time || '09:00',
        end_time: eventSchedule.end_time || ''
      }]);
    } else if (!eventSchedule || Object.keys(eventSchedule).length === 0) {
      // Reset to default when eventSchedule is empty
      setSingleEventDates([{ date: '', start_time: '09:00', end_time: '' }]);
    }
    
    // Mark as saved when data is loaded
    const hasData = eventSchedule && Object.keys(eventSchedule).length > 0;
    const hasOpeningHours = openingHours && openingHours.length > 0;
    
    if (hasData || hasOpeningHours) {
      setTimeDataSaved(true);
      setHasUnsavedChanges(false);
    } else {
      setTimeDataSaved(false);
    }
    
    // Determine which type of recurrence is set
    if (eventSchedule?.recurrence) {
      const recurrence = eventSchedule.recurrence;
      if (recurrence === RECURRENCE_PATTERNS.DAILY) {
        setRecurrenceType(RECURRENCE_TYPES.DAILY);
        setDailySchedule({
          start_time: eventSchedule.start_time || '',
          end_time: eventSchedule.end_time || '',
          recurrence: eventSchedule.recurrence,
          additional_times: eventSchedule.additional_times || []
        });
      } else if ([RECURRENCE_PATTERNS.WEEKLY, RECURRENCE_PATTERNS.BIWEEKLY, RECURRENCE_PATTERNS.TRIWEEKLY, RECURRENCE_PATTERNS.FOURWEEKLY].includes(recurrence)) {
        setRecurrenceType(RECURRENCE_TYPES.WEEKLY);
        setWeeklySchedule({
          day_of_week: eventSchedule.day_of_week || '',
          start_time: eventSchedule.start_time || '',
          end_time: eventSchedule.end_time || '',
          recurrence: eventSchedule.recurrence,
          additional_times: eventSchedule.additional_times || []
        });
      } else if (recurrence.startsWith('monthly_')) {
        setRecurrenceType(RECURRENCE_TYPES.MONTHLY);
        setMonthlySchedule({
          day_of_week: eventSchedule.day_of_week || '',
          start_time: eventSchedule.start_time || '',
          end_time: eventSchedule.end_time || '',
          recurrence: eventSchedule.recurrence,
          additional_times: eventSchedule.additional_times || []
        });
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventSchedule, openingHours]);

  // Reset saved status when event type changes (but not on initial mount)
  useEffect(() => {
    if (prevEventType.current === eventType) {
      return; // Skip: eventType has not actually changed (e.g. StrictMode double-invoke)
    }
    prevEventType.current = eventType;
    // Only reset when user actively changes event type
    setTimeDataSaved(false);
    setHasUnsavedChanges(false);
    setDailyValidated(false);
    setWeeklyValidated(false);
    setMonthlyValidated(false);
    setSingleEventValidated(false);
    setFreelyAccessibleValidated(false);
  }, [eventType]);

  // Notify parent about save status changes
  useEffect(() => {
    if (onSaveStatusChange) {
      onSaveStatusChange(timeDataSaved);
    }
  }, [timeDataSaved, onSaveStatusChange]);

  const handleScheduleChange = (updates) => {
    const newSchedule = { ...localSchedule, ...updates };
    setLocalSchedule(newSchedule);
    onEventScheduleChange(newSchedule);
    setHasUnsavedChanges(true);
  };

  const handleAddSingleEventDate = () => {
    const newDates = [...singleEventDates, { date: '', start_time: '09:00', end_time: '' }];
    setSingleEventDates(newDates);
    handleScheduleChange({ dates: newDates });
    setSingleEventValidated(false);
  };

  const handleRemoveSingleEventDate = (index) => {
    if (singleEventDates.length > 1) {
      const newDates = singleEventDates.filter((_, i) => i !== index);
      setSingleEventDates(newDates);
      handleScheduleChange({ dates: newDates });
      setSingleEventValidated(false);
    }
  };

  const handleSingleEventDateChange = (index, field, value) => {
    const newDates = [...singleEventDates];
    newDates[index][field] = value;
    setSingleEventDates(newDates);
    handleScheduleChange({ dates: newDates });
    setSingleEventValidated(false);
  };

  const handleSaveDailySchedule = () => {
    setSaving(true);
    setMessage(null);
    
    // Validierung: Startzeit muss vor Endzeit liegen
    if (dailySchedule.start_time && dailySchedule.end_time && dailySchedule.start_time >= dailySchedule.end_time) {
      setMessage({ type: 'error', text: 'Die Startzeit muss vor der Endzeit liegen!' });
      setSaving(false);
      return;
    }
    
    // Validierung: pro Zeitraum muss "Gültig ab" vor "Gültig bis" liegen
    const dailyRangeError = validateDateRanges(localSchedule.date_ranges);
    if (dailyRangeError) {
      setMessage({ type: 'error', text: dailyRangeError });
      setSaving(false);
      return;
    }

    // Validierung: Zusätzliche Uhrzeiten
    for (let i = 0; i < (dailySchedule.additional_times || []).length; i++) {
      const tp = dailySchedule.additional_times[i];
      if (tp.start_time && tp.end_time && tp.start_time >= tp.end_time) {
        setMessage({ type: 'error', text: `Uhrzeit ${i + 2}: Die Startzeit muss vor der Endzeit liegen!` });
        setSaving(false);
        return;
      }
    }

    // Save daily schedule
    const scheduleToSave = {
      start_time: dailySchedule.start_time,
      end_time: dailySchedule.end_time,
      recurrence: dailySchedule.recurrence,
      additional_times: (dailySchedule.additional_times || []).filter(tp => tp.start_time),
      date_ranges: localSchedule.date_ranges || []
    };
    
    setLocalSchedule(scheduleToSave);
    onEventScheduleChange(scheduleToSave);
    setMessage({ type: 'success', text: 'Tägliche Wiederholung validiert!' });
    setTimeDataSaved(true);
    setDailyValidated(true);
    setHasUnsavedChanges(false);
    
    setTimeout(() => {
      setSaving(false);
      setMessage(null);
    }, 2000);
  };

  const handleSaveWeeklySchedule = () => {
    setSaving(true);
    setMessage(null);
    
    // Validierung: Bei mehrwöchigen Wiederholungen ist mind. ein Datumsbereich
    // mit Startdatum erforderlich (dient als Anker für die Wochenberechnung).
    const weeklyHasStart = (localSchedule.date_ranges || []).some(r => r.from);
    if (['biweekly', 'triweekly', 'fourweekly'].includes(weeklySchedule.recurrence) && !weeklyHasStart) {
      setMessage({ type: 'error', text: 'Bei mehrwöchigen Wiederholungen muss mindestens ein Datumsbereich mit Startdatum angegeben werden!' });
      setSaving(false);
      return;
    }

    // Validierung: Startzeit muss vor Endzeit liegen
    if (weeklySchedule.start_time && weeklySchedule.end_time && weeklySchedule.start_time >= weeklySchedule.end_time) {
      setMessage({ type: 'error', text: 'Die Startzeit muss vor der Endzeit liegen!' });
      setSaving(false);
      return;
    }

    // Validierung: pro Zeitraum muss "Gültig ab" vor "Gültig bis" liegen
    const weeklyRangeError = validateDateRanges(localSchedule.date_ranges);
    if (weeklyRangeError) {
      setMessage({ type: 'error', text: weeklyRangeError });
      setSaving(false);
      return;
    }

    // Validierung: Zusätzliche Uhrzeiten
    for (let i = 0; i < (weeklySchedule.additional_times || []).length; i++) {
      const tp = weeklySchedule.additional_times[i];
      if (tp.start_time && tp.end_time && tp.start_time >= tp.end_time) {
        setMessage({ type: 'error', text: `Uhrzeit ${i + 2}: Die Startzeit muss vor der Endzeit liegen!` });
        setSaving(false);
        return;
      }
    }

    // Save weekly schedule and clear monthly values
    const scheduleToSave = {
      day_of_week: weeklySchedule.day_of_week,
      start_time: weeklySchedule.start_time,
      end_time: weeklySchedule.end_time,
      recurrence: weeklySchedule.recurrence,
      additional_times: (weeklySchedule.additional_times || []).filter(tp => tp.start_time),
      date_ranges: localSchedule.date_ranges || []
    };
    
    setLocalSchedule(scheduleToSave);
    onEventScheduleChange(scheduleToSave);
    setMessage({ type: 'success', text: 'Wöchentliche Wiederholung validiert!' });
    setTimeDataSaved(true);
    setWeeklyValidated(true);
    setHasUnsavedChanges(false);
    
    setTimeout(() => {
      setSaving(false);
      setMessage(null);
    }, 2000);
  };

  const handleSaveMonthlySchedule = () => {
    setSaving(true);
    setMessage(null);
    
    // Validierung: Startzeit muss vor Endzeit liegen
    if (monthlySchedule.start_time && monthlySchedule.end_time && monthlySchedule.start_time >= monthlySchedule.end_time) {
      setMessage({ type: 'error', text: 'Die Startzeit muss vor der Endzeit liegen!' });
      setSaving(false);
      return;
    }
    
    // Validierung: pro Zeitraum muss "Gültig ab" vor "Gültig bis" liegen
    const monthlyRangeError = validateDateRanges(localSchedule.date_ranges);
    if (monthlyRangeError) {
      setMessage({ type: 'error', text: monthlyRangeError });
      setSaving(false);
      return;
    }

    // Validierung: Zusätzliche Uhrzeiten
    for (let i = 0; i < (monthlySchedule.additional_times || []).length; i++) {
      const tp = monthlySchedule.additional_times[i];
      if (tp.start_time && tp.end_time && tp.start_time >= tp.end_time) {
        setMessage({ type: 'error', text: `Uhrzeit ${i + 2}: Die Startzeit muss vor der Endzeit liegen!` });
        setSaving(false);
        return;
      }
    }

    // Save monthly schedule and clear weekly values
    const scheduleToSave = {
      day_of_week: monthlySchedule.day_of_week,
      start_time: monthlySchedule.start_time,
      end_time: monthlySchedule.end_time,
      recurrence: monthlySchedule.recurrence,
      additional_times: (monthlySchedule.additional_times || []).filter(tp => tp.start_time),
      date_ranges: localSchedule.date_ranges || []
    };
    
    setLocalSchedule(scheduleToSave);
    onEventScheduleChange(scheduleToSave);
    setMessage({ type: 'success', text: 'Monatliche Wiederholung validiert!' });
    setTimeDataSaved(true);
    setMonthlyValidated(true);
    setHasUnsavedChanges(false);
    
    setTimeout(() => {
      setSaving(false);
      setMessage(null);
    }, 2000);
  };

  const getMonthlyRecurrenceLabel = (pattern) => {
    const weekdayName = monthlySchedule.day_of_week ? DAYS_DE[monthlySchedule.day_of_week] : '[Wochentag]';
    const labels = {
      [RECURRENCE_PATTERNS.MONTHLY_FIRST]: `Jeden ersten ${weekdayName} im Monat`,
      [RECURRENCE_PATTERNS.MONTHLY_SECOND]: `Jeden zweiten ${weekdayName} im Monat`,
      [RECURRENCE_PATTERNS.MONTHLY_THIRD]: `Jeden dritten ${weekdayName} im Monat`,
      [RECURRENCE_PATTERNS.MONTHLY_FOURTH]: `Jeden vierten ${weekdayName} im Monat`,
      [RECURRENCE_PATTERNS.MONTHLY_LAST]: `Jeden letzten ${weekdayName} im Monat`
    };
    return labels[pattern] || pattern;
  };

  const handleOpeningHoursSave = async (hours) => {
    onOpeningHoursChange(hours);
    setTimeDataSaved(true);
    setHasUnsavedChanges(false);
    return Promise.resolve();
  };

  const handleSaveSingleEvent = () => {
    setSaving(true);
    setMessage(null);
    
    // Validierung: Startzeit muss vor Endzeit liegen (wenn beide angegeben)
    for (let i = 0; i < singleEventDates.length; i++) {
      const eventDate = singleEventDates[i];
      if (eventDate.start_time && eventDate.end_time && eventDate.start_time >= eventDate.end_time) {
        setMessage({ type: 'error', text: `Termin ${i + 1}: Die Startzeit muss vor der Endzeit liegen!` });
        setSaving(false);
        return;
      }
    }
    
    const scheduleToSave = { dates: singleEventDates };
    setLocalSchedule(scheduleToSave);
    onEventScheduleChange(scheduleToSave);
    setMessage({ type: 'success', text: 'Einzeltermin(e) validiert!' });
    setTimeDataSaved(true);
    setSingleEventValidated(true);
    setHasUnsavedChanges(false);
    
    setTimeout(() => {
      setSaving(false);
      setMessage(null);
    }, 2000);
  };

  const handleSaveFreelyAccessible = () => {
    setSaving(true);
    setMessage(null);
    
    // Validierung: pro Zeitraum muss "Offen ab" vor "Offen bis" liegen
    const rangeError = validateDateRanges(localSchedule.date_ranges);
    if (rangeError) {
      setMessage({ type: 'error', text: rangeError });
      setSaving(false);
      return;
    }

    setTimeDataSaved(true);
    setFreelyAccessibleValidated(true);
    setHasUnsavedChanges(false);
    setMessage({ type: 'success', text: 'Zugänglichkeitszeiten validiert!' });
    
    setTimeout(() => {
      setSaving(false);
      setMessage(null);
    }, 2000);
  };

  const isSingleEventValid = () => {
    // At least one date must be filled
    return singleEventDates.some(d => d.date && d.date.trim() !== '');
  };

  const handleValidityChange = (validityData) => {
    // Save validity data in eventSchedule when using regular hours
    const scheduleUpdate = {
      ...localSchedule,
      date_ranges: validityData.dateRanges || [],
      isSeasonal: validityData.isSeasonal || false
    };
    setLocalSchedule(scheduleUpdate);
    onEventScheduleChange(scheduleUpdate);
  };

  const handleOpeningHoursImport = async () => {
    if (onImportOpeningHours) {
      const result = await onImportOpeningHours();
      return result;
    }
    throw new Error('Import nicht verfügbar');
  };

  return (
    <div className="event-schedule-manager">
      <div className="event-type-selector">
        <div className="event-type-label-row">
          <label className="block mb-2 font-medium">Zeitangaben</label>
          <FieldInfo>
            <>
              Events erfordern je nach Typ unterschiedliche Formate für Zeitangaben.
              Details und Beispiele finden Sie auf der Seite{' '}
              <a href="/zeitangaben-formate" style={{ color: 'var(--color-primary)', textDecoration: 'underline' }}>
                Formate für Zeitangaben
              </a>
              .
            </>
          </FieldInfo>
        </div>
        <div className="event-type-options">
          <button
            type="button"
            className={`event-type-btn ${eventType === EVENT_TYPES.REGULAR_HOURS ? 'active' : ''}`}
            onClick={() => onEventTypeChange(EVENT_TYPES.REGULAR_HOURS)}
          >
            <span className="event-type-icon">🕒</span>
            <span>Öffnungszeiten</span>
          </button>
          <button
            type="button"
            className={`event-type-btn ${eventType === EVENT_TYPES.RECURRING_EVENT ? 'active' : ''}`}
            onClick={() => onEventTypeChange(EVENT_TYPES.RECURRING_EVENT)}
          >
            <span className="event-type-icon">🔄</span>
            <span>Wiederkehrend</span>
          </button>
          <button
            type="button"
            className={`event-type-btn ${eventType === EVENT_TYPES.SINGLE_EVENT ? 'active' : ''}`}
            onClick={() => onEventTypeChange(EVENT_TYPES.SINGLE_EVENT)}
          >
            <span className="event-type-icon">📅</span>
            <span>Einzeltermin</span>
          </button>
          <button
            type="button"
            className={`event-type-btn ${eventType === EVENT_TYPES.MULTI_DAY ? 'active' : ''}`}
            onClick={() => onEventTypeChange(EVENT_TYPES.MULTI_DAY)}
          >
            <span className="event-type-icon">📆</span>
            <span>Mehrtägig</span>
          </button>
          <button
            type="button"
            className={`event-type-btn ${eventType === EVENT_TYPES.FREELY_ACCESSIBLE ? 'active' : ''}`}
            onClick={() => onEventTypeChange(EVENT_TYPES.FREELY_ACCESSIBLE)}
          >
            <span className="event-type-icon">🌐</span>
            <span>Frei zugänglich</span>
          </button>
        </div>
      </div>

      {/* Regular Opening Hours */}
      {eventType === EVENT_TYPES.REGULAR_HOURS && (
        <div className="schedule-content">
          <p className="text-sm text-gray-600 mb-3">
            Geben Sie die regulären Öffnungszeiten an oder importieren Sie diese automatisch.
          </p>
          
          {/* Website Field */}
          <div className="mb-4">
            <label className="block text-sm font-medium mb-2">Webseite der Location</label>
            <div className="flex gap-2">
              <input
                type="url"
                value={website || ''}
                onChange={(e) => onWebsiteChange && onWebsiteChange(e.target.value)}
                placeholder="https://www.beispiel.ch"
                className="input flex-1"
              />
              {website && onImportFromWebsite && (
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      await onImportFromWebsite();
                    } catch (err) {
                      alert('Fehler beim Importieren: ' + err.message);
                    }
                  }}
                  className="btn btn-secondary whitespace-nowrap"
                >
                  🌐 Extrahiere Besuchszeiten
                </button>
              )}
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Die Öffnungszeiten können von der angegebenen Webseite automatisch extrahiert werden.
            </p>
          </div>

          <OpeningHoursEditor
            openingHours={openingHours}
            onSave={handleOpeningHoursSave}
            onImport={handleOpeningHoursImport}
            canImport={canImportFromGoogle && !!address}
            dateRanges={localSchedule.date_ranges || []}
            isSeasonal={isSeasonal || false}
            isEditMode={isEditMode}
            onValidityChange={(validityData) => {
              handleValidityChange(validityData);
              if (onSeasonalChange) {
                onSeasonalChange(validityData.isSeasonal);
              }
            }}
          />
        </div>
      )}

      {/* Recurring Event */}
      {eventType === EVENT_TYPES.RECURRING_EVENT && (
        <div className="schedule-content">
          <p className="text-sm text-gray-600 mb-4">
            Konfigurieren Sie einen wiederkehrenden Termin.
          </p>

          {message && (
            <div className={`message message-${message.type} mb-4`}>
              {message.text}
            </div>
          )}

          {/* Recurrence Type Selector */}
          <div className="recurrence-type-selector mb-4">
            <label className="block text-sm font-medium mb-2">Art der Wiederholung</label>
            <div className="recurrence-type-options">
              <button
                type="button"
                className={`recurrence-type-btn ${recurrenceType === RECURRENCE_TYPES.DAILY ? 'active' : ''}`}
                onClick={() => setRecurrenceType(RECURRENCE_TYPES.DAILY)}
              >
                <span className="recurrence-type-icon">🌞</span>
                <span className="recurrence-type-text">Täglich</span>
              </button>
              <button
                type="button"
                className={`recurrence-type-btn ${recurrenceType === RECURRENCE_TYPES.WEEKLY ? 'active' : ''}`}
                onClick={() => setRecurrenceType(RECURRENCE_TYPES.WEEKLY)}
              >
                <span className="recurrence-type-icon">📅</span>
                <span className="recurrence-type-text">Wöchentlich</span>
              </button>
              <button
                type="button"
                className={`recurrence-type-btn ${recurrenceType === RECURRENCE_TYPES.MONTHLY ? 'active' : ''}`}
                onClick={() => setRecurrenceType(RECURRENCE_TYPES.MONTHLY)}
              >
                <span className="recurrence-type-icon">📆</span>
                <span className="recurrence-type-text">Monatlich</span>
              </button>
            </div>
          </div>

          {/* Daily Recurrence */}
          {recurrenceType === RECURRENCE_TYPES.DAILY && (
            <div className="recurrence-form">
              <h3>Tägliche Wiederholung</h3>
              
              <div className="space-y-4">
                <div className="single-event-date-group">
                  <div className="single-event-date-header">
                    <label className="block text-sm font-medium mb-2">Uhrzeit 1</label>
                    <div className="single-event-date-actions">
                      {(dailySchedule.additional_times || []).length === 0 && (
                        <button
                          type="button"
                          onClick={() => {
                            setDailySchedule({
                              ...dailySchedule,
                              additional_times: [...(dailySchedule.additional_times || []), { start_time: '', end_time: '' }]
                            });
                            setDailyValidated(false);
                          }}
                          className="icon-btn icon-btn-add"
                          title="Weitere Uhrzeit hinzufügen"
                        >
                          <Plus size={18} />
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">Startzeit</label>
                      <TimeInput
                                                value={dailySchedule.start_time}
                        onChange={(e) => {
                          setDailySchedule({ ...dailySchedule, start_time: e.target.value });
                          setDailyValidated(false);
                        }}
                        className="input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Endzeit (optional)</label>
                      <TimeInput
                                                value={dailySchedule.end_time}
                        onChange={(e) => {
                          setDailySchedule({ ...dailySchedule, end_time: e.target.value });
                          setDailyValidated(false);
                        }}
                        className="input"
                      />
                    </div>
                  </div>
                </div>

                {/* Zusätzliche Uhrzeiten (täglich) */}
                {(dailySchedule.additional_times || []).map((tp, idx) => (
                  <div key={idx} className="single-event-date-group">
                    <div className="single-event-date-header">
                      <label className="block text-sm font-medium mb-2">Uhrzeit {idx + 2}</label>
                      <div className="single-event-date-actions">
                        {idx === (dailySchedule.additional_times || []).length - 1 && (
                          <button
                            type="button"
                            onClick={() => {
                              setDailySchedule({
                                ...dailySchedule,
                                additional_times: [...(dailySchedule.additional_times || []), { start_time: '', end_time: '' }]
                              });
                              setDailyValidated(false);
                            }}
                            className="icon-btn icon-btn-add"
                            title="Weitere Uhrzeit hinzufügen"
                          >
                            <Plus size={18} />
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => {
                            const times = dailySchedule.additional_times.filter((_, i) => i !== idx);
                            setDailySchedule({ ...dailySchedule, additional_times: times });
                            setDailyValidated(false);
                          }}
                          className="icon-btn icon-btn-remove"
                          title="Uhrzeit entfernen"
                        >
                          <X size={18} />
                        </button>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-2">Startzeit {idx + 2}</label>
                        <TimeInput
                                                    value={tp.start_time}
                          onChange={(e) => {
                            const times = [...dailySchedule.additional_times];
                            times[idx] = { ...times[idx], start_time: e.target.value };
                            setDailySchedule({ ...dailySchedule, additional_times: times });
                            setDailyValidated(false);
                          }}
                          className="input"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">Endzeit {idx + 2} (optional)</label>
                        <TimeInput
                                                    value={tp.end_time}
                          onChange={(e) => {
                            const times = [...dailySchedule.additional_times];
                            times[idx] = { ...times[idx], end_time: e.target.value };
                            setDailySchedule({ ...dailySchedule, additional_times: times });
                            setDailyValidated(false);
                          }}
                          className="input"
                        />
                      </div>
                    </div>
                  </div>
                ))}

                <div>
                  <label className="block text-sm font-medium mb-2">
                    <input
                      type="checkbox"
                      checked={isSeasonal || false}
                      onChange={(e) => onSeasonalChange && onSeasonalChange(e.target.checked)}
                      className="mr-2"
                    />
                    Saisonale Aktivität
                  </label>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">
                    Gültig in folgenden Zeiträumen (optional)
                  </label>
                  <DateRangeListEditor
                    ranges={localSchedule.date_ranges || []}
                    onChange={(ranges) => {
                      handleScheduleChange({ date_ranges: ranges });
                      setDailyValidated(false);
                    }}
                    labelFrom="Gültig ab"
                    labelUntil="Gültig bis"
                  />
                </div>

                <button
                  type="button"
                  onClick={handleSaveDailySchedule}
                  disabled={saving || dailyValidated || !dailySchedule.start_time}
                  className="btn btn-primary"
                >
                  {saving ? 'Validiere...' : dailyValidated ? 'Validiert' : 'Validieren'}
                </button>
                {!dailyValidated && (
                  <p className="validate-hint">Validieren für die Erstellung der Aktivität erforderlich.</p>
                )}
              </div>
            </div>
          )}

          {/* Weekly Recurrence */}
          {recurrenceType === RECURRENCE_TYPES.WEEKLY && (
            <div className="recurrence-form">
              <h3>Wöchentliche Wiederholung</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Wochentag</label>
                  <select
                    value={weeklySchedule.day_of_week}
                    onChange={(e) => {
                      setWeeklySchedule({ ...weeklySchedule, day_of_week: e.target.value });
                      setWeeklyValidated(false);
                    }}
                    className="input"
                  >
                    <option value="">Bitte wählen...</option>
                    {Object.entries(DAYS_DE).map(([key, label]) => (
                      <option key={key} value={key}>{label}</option>
                    ))}
                  </select>
                </div>

                <div className="single-event-date-group">
                  <div className="single-event-date-header">
                    <label className="block text-sm font-medium mb-2">Uhrzeit 1</label>
                    <div className="single-event-date-actions">
                      {(weeklySchedule.additional_times || []).length === 0 && (
                        <button
                          type="button"
                          onClick={() => {
                            setWeeklySchedule({
                              ...weeklySchedule,
                              additional_times: [...(weeklySchedule.additional_times || []), { start_time: '', end_time: '' }]
                            });
                            setWeeklyValidated(false);
                          }}
                          className="icon-btn icon-btn-add"
                          title="Weitere Uhrzeit hinzufügen"
                        >
                          <Plus size={18} />
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">Startzeit</label>
                      <TimeInput
                                                value={weeklySchedule.start_time}
                        onChange={(e) => {
                          setWeeklySchedule({ ...weeklySchedule, start_time: e.target.value });
                          setWeeklyValidated(false);
                        }}
                        className="input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Endzeit (optional)</label>
                      <TimeInput
                                                value={weeklySchedule.end_time}
                        onChange={(e) => {
                          setWeeklySchedule({ ...weeklySchedule, end_time: e.target.value });
                          setWeeklyValidated(false);
                        }}
                        className="input"
                      />
                    </div>
                  </div>
                </div>

                {/* Zusätzliche Uhrzeiten (wöchentlich) */}
                {(weeklySchedule.additional_times || []).map((tp, idx) => (
                  <div key={idx} className="single-event-date-group">
                    <div className="single-event-date-header">
                      <label className="block text-sm font-medium mb-2">Uhrzeit {idx + 2}</label>
                      <div className="single-event-date-actions">
                        {idx === (weeklySchedule.additional_times || []).length - 1 && (
                          <button
                            type="button"
                            onClick={() => {
                              setWeeklySchedule({
                                ...weeklySchedule,
                                additional_times: [...(weeklySchedule.additional_times || []), { start_time: '', end_time: '' }]
                              });
                              setWeeklyValidated(false);
                            }}
                            className="icon-btn icon-btn-add"
                            title="Weitere Uhrzeit hinzufügen"
                          >
                            <Plus size={18} />
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => {
                            const times = weeklySchedule.additional_times.filter((_, i) => i !== idx);
                            setWeeklySchedule({ ...weeklySchedule, additional_times: times });
                            setWeeklyValidated(false);
                          }}
                          className="icon-btn icon-btn-remove"
                          title="Uhrzeit entfernen"
                        >
                          <X size={18} />
                        </button>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-2">Startzeit {idx + 2}</label>
                        <TimeInput
                                                    value={tp.start_time}
                          onChange={(e) => {
                            const times = [...weeklySchedule.additional_times];
                            times[idx] = { ...times[idx], start_time: e.target.value };
                            setWeeklySchedule({ ...weeklySchedule, additional_times: times });
                            setWeeklyValidated(false);
                          }}
                          className="input"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">Endzeit {idx + 2} (optional)</label>
                        <TimeInput
                                                    value={tp.end_time}
                          onChange={(e) => {
                            const times = [...weeklySchedule.additional_times];
                            times[idx] = { ...times[idx], end_time: e.target.value };
                            setWeeklySchedule({ ...weeklySchedule, additional_times: times });
                            setWeeklyValidated(false);
                          }}
                          className="input"
                        />
                      </div>
                    </div>
                  </div>
                ))}

                <div>
                  <label className="block text-sm font-medium mb-2">Wiederholung</label>
                  <select
                    value={weeklySchedule.recurrence}
                    onChange={(e) => {
                      setWeeklySchedule({ ...weeklySchedule, recurrence: e.target.value });
                      setWeeklyValidated(false);
                    }}
                    className="input"
                  >
                    <option value={RECURRENCE_PATTERNS.WEEKLY}>jede Woche</option>
                    <option value={RECURRENCE_PATTERNS.BIWEEKLY}>jede 2. Woche</option>
                    <option value={RECURRENCE_PATTERNS.TRIWEEKLY}>jede 3. Woche</option>
                    <option value={RECURRENCE_PATTERNS.FOURWEEKLY}>jede 4. Woche</option>
                  </select>
                  {['biweekly', 'triweekly', 'fourweekly'].includes(weeklySchedule.recurrence) && (
                    <p className="text-xs text-orange-600 mt-1">
                      ⚠️ Bei mehrwöchigen Wiederholungen muss mindestens ein Datumsbereich mit Startdatum angegeben werden.
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">
                    <input
                      type="checkbox"
                      checked={isSeasonal || false}
                      onChange={(e) => onSeasonalChange && onSeasonalChange(e.target.checked)}
                      className="mr-2"
                    />
                    Saisonale Aktivität
                  </label>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">
                    Gültig in folgenden Zeiträumen{' '}
                    {['biweekly', 'triweekly', 'fourweekly'].includes(weeklySchedule.recurrence) && (
                      <span className="text-red-500">*</span>
                    )}
                  </label>
                  <DateRangeListEditor
                    ranges={localSchedule.date_ranges || []}
                    onChange={(ranges) => {
                      handleScheduleChange({ date_ranges: ranges });
                      setWeeklyValidated(false);
                    }}
                    labelFrom="Gültig ab"
                    labelUntil="Gültig bis"
                    fromRequired={['biweekly', 'triweekly', 'fourweekly'].includes(weeklySchedule.recurrence)}
                  />
                </div>

                <button
                  type="button"
                  onClick={handleSaveWeeklySchedule}
                  disabled={
                    saving || 
                    weeklyValidated ||
                    !weeklySchedule.day_of_week || 
                    !weeklySchedule.start_time
                  }
                  className="btn btn-primary"
                >
                  {saving ? 'Validiere...' : weeklyValidated ? 'Validiert' : 'Validieren'}
                </button>
                {!weeklyValidated && (
                  <p className="validate-hint">Validieren für die Erstellung der Aktivität erforderlich.</p>
                )}
              </div>
            </div>
          )}

          {/* Monthly Recurrence */}
          {recurrenceType === RECURRENCE_TYPES.MONTHLY && (
            <div className="recurrence-form">
              <h3>Monatliche Wiederholung</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Wochentag</label>
                  <select
                    value={monthlySchedule.day_of_week}
                    onChange={(e) => {
                      setMonthlySchedule({ ...monthlySchedule, day_of_week: e.target.value });
                      setMonthlyValidated(false);
                    }}
                    className="input"
                  >
                    <option value="">Bitte wählen...</option>
                    {Object.entries(DAYS_DE).map(([key, label]) => (
                      <option key={key} value={key}>{label}</option>
                    ))}
                  </select>
                </div>

                <div className="single-event-date-group">
                  <div className="single-event-date-header">
                    <label className="block text-sm font-medium mb-2">Uhrzeit 1</label>
                    <div className="single-event-date-actions">
                      {(monthlySchedule.additional_times || []).length === 0 && (
                        <button
                          type="button"
                          onClick={() => {
                            setMonthlySchedule({
                              ...monthlySchedule,
                              additional_times: [...(monthlySchedule.additional_times || []), { start_time: '', end_time: '' }]
                            });
                            setMonthlyValidated(false);
                          }}
                          className="icon-btn icon-btn-add"
                          title="Weitere Uhrzeit hinzufügen"
                        >
                          <Plus size={18} />
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">Startzeit</label>
                      <TimeInput
                                                value={monthlySchedule.start_time}
                        onChange={(e) => {
                          setMonthlySchedule({ ...monthlySchedule, start_time: e.target.value });
                          setMonthlyValidated(false);
                        }}
                        className="input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Endzeit (optional)</label>
                      <TimeInput
                                                value={monthlySchedule.end_time}
                        onChange={(e) => {
                          setMonthlySchedule({ ...monthlySchedule, end_time: e.target.value });
                          setMonthlyValidated(false);
                        }}
                        className="input"
                      />
                    </div>
                  </div>
                </div>

                {/* Zusätzliche Uhrzeiten (monatlich) */}
                {(monthlySchedule.additional_times || []).map((tp, idx) => (
                  <div key={idx} className="single-event-date-group">
                    <div className="single-event-date-header">
                      <label className="block text-sm font-medium mb-2">Uhrzeit {idx + 2}</label>
                      <div className="single-event-date-actions">
                        {idx === (monthlySchedule.additional_times || []).length - 1 && (
                          <button
                            type="button"
                            onClick={() => {
                              setMonthlySchedule({
                                ...monthlySchedule,
                                additional_times: [...(monthlySchedule.additional_times || []), { start_time: '', end_time: '' }]
                              });
                              setMonthlyValidated(false);
                            }}
                            className="icon-btn icon-btn-add"
                            title="Weitere Uhrzeit hinzufügen"
                          >
                            <Plus size={18} />
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => {
                            const times = monthlySchedule.additional_times.filter((_, i) => i !== idx);
                            setMonthlySchedule({ ...monthlySchedule, additional_times: times });
                            setMonthlyValidated(false);
                          }}
                          className="icon-btn icon-btn-remove"
                          title="Uhrzeit entfernen"
                        >
                          <X size={18} />
                        </button>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-2">Startzeit {idx + 2}</label>
                        <TimeInput
                                                    value={tp.start_time}
                          onChange={(e) => {
                            const times = [...monthlySchedule.additional_times];
                            times[idx] = { ...times[idx], start_time: e.target.value };
                            setMonthlySchedule({ ...monthlySchedule, additional_times: times });
                            setMonthlyValidated(false);
                          }}
                          className="input"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">Endzeit {idx + 2} (optional)</label>
                        <TimeInput
                                                    value={tp.end_time}
                          onChange={(e) => {
                            const times = [...monthlySchedule.additional_times];
                            times[idx] = { ...times[idx], end_time: e.target.value };
                            setMonthlySchedule({ ...monthlySchedule, additional_times: times });
                            setMonthlyValidated(false);
                          }}
                          className="input"
                        />
                      </div>
                    </div>
                  </div>
                ))}

                <div>
                  <label className="block text-sm font-medium mb-2">Wiederholung</label>
                  <select
                    value={monthlySchedule.recurrence}
                    onChange={(e) => setMonthlySchedule({ ...monthlySchedule, recurrence: e.target.value })}
                    className="input"
                  >
                    <option value={RECURRENCE_PATTERNS.MONTHLY_FIRST}>
                      {getMonthlyRecurrenceLabel(RECURRENCE_PATTERNS.MONTHLY_FIRST)}
                    </option>
                    <option value={RECURRENCE_PATTERNS.MONTHLY_SECOND}>
                      {getMonthlyRecurrenceLabel(RECURRENCE_PATTERNS.MONTHLY_SECOND)}
                    </option>
                    <option value={RECURRENCE_PATTERNS.MONTHLY_THIRD}>
                      {getMonthlyRecurrenceLabel(RECURRENCE_PATTERNS.MONTHLY_THIRD)}
                    </option>
                    <option value={RECURRENCE_PATTERNS.MONTHLY_FOURTH}>
                      {getMonthlyRecurrenceLabel(RECURRENCE_PATTERNS.MONTHLY_FOURTH)}
                    </option>
                    <option value={RECURRENCE_PATTERNS.MONTHLY_LAST}>
                      {getMonthlyRecurrenceLabel(RECURRENCE_PATTERNS.MONTHLY_LAST)}
                    </option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">
                    <input
                      type="checkbox"
                      checked={isSeasonal || false}
                      onChange={(e) => onSeasonalChange && onSeasonalChange(e.target.checked)}
                      className="mr-2"
                    />
                    Saisonale Aktivität
                  </label>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">
                    Gültig in folgenden Zeiträumen (optional)
                  </label>
                  <DateRangeListEditor
                    ranges={localSchedule.date_ranges || []}
                    onChange={(ranges) => {
                      handleScheduleChange({ date_ranges: ranges });
                      setMonthlyValidated(false);
                    }}
                    labelFrom="Gültig ab"
                    labelUntil="Gültig bis"
                  />
                </div>

                <button
                  type="button"
                  onClick={handleSaveMonthlySchedule}
                  disabled={saving || monthlyValidated || !monthlySchedule.day_of_week || !monthlySchedule.start_time}
                  className="btn btn-primary"
                >
                  {saving ? 'Validiere...' : monthlyValidated ? 'Validiert' : 'Validieren'}
                </button>
                {!monthlyValidated && (
                  <p className="validate-hint">Validieren für die Erstellung der Aktivität erforderlich.</p>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Single Event */}
      {eventType === EVENT_TYPES.SINGLE_EVENT && (
        <div className="schedule-content">
          <p className="text-sm text-gray-600 mb-4">
            Geben Sie ein oder mehrere Daten für Ihre Veranstaltung an.
          </p>

          {message && (
            <div className={`message message-${message.type} mb-4`}>
              {message.text}
            </div>
          )}
          
          <div className="space-y-4">
            {singleEventDates.map((eventDate, index) => (
              <div key={index} className="single-event-date-group">
                <div className="single-event-date-header">
                  <label className="block text-sm font-medium mb-2">
                    {singleEventDates.length > 1 ? `Termin ${index + 1}` : 'Datum'}
                  </label>
                  <div className="single-event-date-actions">
                    {index === singleEventDates.length - 1 && (
                      <button
                        type="button"
                        onClick={handleAddSingleEventDate}
                        className="icon-btn icon-btn-add"
                        title="Weiteres Datum hinzufügen"
                      >
                        <Plus size={18} />
                      </button>
                    )}
                    {singleEventDates.length > 1 && (
                      <button
                        type="button"
                        onClick={() => handleRemoveSingleEventDate(index)}
                        className="icon-btn icon-btn-remove"
                        title="Datum entfernen"
                      >
                        <X size={18} />
                      </button>
                    )}
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <input
                      type="date"
                      value={eventDate.date || ''}
                      onChange={(e) => handleSingleEventDateChange(index, 'date', e.target.value)}
                      className="input"
                      required
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">Startzeit</label>
                      <TimeInput
                                                value={eventDate.start_time || '09:00'}
                        onChange={(e) => handleSingleEventDateChange(index, 'start_time', e.target.value)}
                        className="input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Endzeit (optional)</label>
                      <TimeInput
                                                value={eventDate.end_time || ''}
                        onChange={(e) => handleSingleEventDateChange(index, 'end_time', e.target.value)}
                        className="input"
                      />
                    </div>
                  </div>
                </div>
              </div>
            ))}

            <button
              type="button"
              onClick={handleSaveSingleEvent}
              disabled={saving || singleEventValidated || !isSingleEventValid()}
              className="btn btn-primary mt-4"
            >
              {saving ? 'Validiere...' : singleEventValidated ? 'Validiert' : 'Validieren'}
            </button>
            {!singleEventValidated && (
              <p className="validate-hint">Validieren für die Erstellung der Aktivität erforderlich.</p>
            )}
          </div>
        </div>
      )}

      {/* Multi-Day Event */}
      {eventType === EVENT_TYPES.MULTI_DAY && (
        <div className="schedule-content">
          <p className="text-sm text-gray-600 mb-4">
            Geben Sie mehrere Daten für Ihre mehrtägige Veranstaltung an. Der Event erscheint als Activity Card nur für den ersten Termin.
          </p>

          {message && (
            <div className={`message message-${message.type} mb-4`}>
              {message.text}
            </div>
          )}
          
          <div className="space-y-4">
            {singleEventDates.map((eventDate, index) => (
              <div key={index} className="single-event-date-group">
                <div className="single-event-date-header">
                  <label className="block text-sm font-medium mb-2">
                    {singleEventDates.length > 1 ? `Termin ${index + 1}` : 'Datum'}
                  </label>
                  <div className="single-event-date-actions">
                    {index === singleEventDates.length - 1 && (
                      <button
                        type="button"
                        onClick={handleAddSingleEventDate}
                        className="icon-btn icon-btn-add"
                        title="Weiteres Datum hinzufügen"
                      >
                        <Plus size={18} />
                      </button>
                    )}
                    {singleEventDates.length > 1 && (
                      <button
                        type="button"
                        onClick={() => handleRemoveSingleEventDate(index)}
                        className="icon-btn icon-btn-remove"
                        title="Datum entfernen"
                      >
                        <X size={18} />
                      </button>
                    )}
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <input
                      type="date"
                      value={eventDate.date || ''}
                      onChange={(e) => handleSingleEventDateChange(index, 'date', e.target.value)}
                      className="input"
                      required
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">Startzeit</label>
                      <TimeInput
                                                value={eventDate.start_time || '09:00'}
                        onChange={(e) => handleSingleEventDateChange(index, 'start_time', e.target.value)}
                        className="input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Endzeit (optional)</label>
                      <TimeInput
                                                value={eventDate.end_time || ''}
                        onChange={(e) => handleSingleEventDateChange(index, 'end_time', e.target.value)}
                        className="input"
                      />
                    </div>
                  </div>
                </div>
              </div>
            ))}

            <button
              type="button"
              onClick={handleSaveSingleEvent}
              disabled={saving || singleEventValidated || !isSingleEventValid()}
              className="btn btn-primary mt-4"
            >
              {saving ? 'Validiere...' : singleEventValidated ? 'Validiert' : 'Validieren'}
            </button>
            {!singleEventValidated && (
              <p className="validate-hint">Validieren für die Erstellung der Aktivität erforderlich.</p>
            )}
          </div>
        </div>
      )}

      {/* Freely Accessible */}
      {eventType === EVENT_TYPES.FREELY_ACCESSIBLE && (
        <div className="schedule-content">
          <p className="text-sm text-gray-600 mb-4">
            Dieser Event ist im gegebenen Zeitraum frei zugänglich.
          </p>

          {message && (
            <div className={`message message-${message.type} mb-4`}>
              {message.text}
            </div>
          )}
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">
                <input
                  type="checkbox"
                  checked={isSeasonal || false}
                  onChange={(e) => onSeasonalChange && onSeasonalChange(e.target.checked)}
                  className="mr-2"
                />
                Saisonale Aktivität
              </label>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">
                Zugänglich in folgenden Zeiträumen (optional)
              </label>
              <p className="text-xs text-gray-500 mb-2">
                Ohne Angabe ist der Event jederzeit zugänglich. Mehrere Zeiträume,
                z.B. für Schulferien, sind möglich.
              </p>
              <DateRangeListEditor
                ranges={localSchedule.date_ranges || []}
                onChange={(ranges) => {
                  handleScheduleChange({ date_ranges: ranges });
                  setFreelyAccessibleValidated(false);
                }}
                labelFrom="Offen ab"
                labelUntil="Offen bis"
              />
            </div>

            <button
              type="button"
              onClick={handleSaveFreelyAccessible}
              disabled={saving || freelyAccessibleValidated}
              className="btn btn-primary mt-4"
            >
              {saving ? 'Validiere...' : freelyAccessibleValidated ? 'Validiert' : 'Validieren'}
            </button>
            {!freelyAccessibleValidated && (
              <p className="validate-hint">Validieren für die Erstellung der Aktivität erforderlich.</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default EventScheduleManager;
